import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Copy, Calendar, Percent, DollarSign, Gift, Tag,
  Clock, CheckCircle, XCircle, Info, ExternalLink
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const UserCoupons = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('available');
  const [copiedCouponId, setCopiedCouponId] = useState(null);

  // Fetch user coupons
  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await api.get('/coupons');
      if (response.data.success) {
        setCoupons(response.data.data.coupons);
      }
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  // Copy coupon code with inline feedback
  const copyCode = async (code, couponId) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedCouponId(couponId);
      setTimeout(() => setCopiedCouponId(null), 2500);
      toast.success('Coupon code copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy coupon code. Please copy manually: ' + code);
    }
  };

  // Get coupon type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'percentage':
        return <Percent className="w-8 h-8 text-green-600" />;
      case 'fixed_amount':
        return <DollarSign className="w-8 h-8 text-blue-600" />;
      case 'buy_one_get_one':
        return <Gift className="w-8 h-8 text-purple-600" />;
      case 'free_shipping':
        return <Tag className="w-8 h-8 text-orange-600" />;
      default:
        return <Tag className="w-8 h-8 text-gray-600" />;
    }
  };

  // Get status badge
  const getStatusBadge = (coupon) => {
    const now = new Date();
    const startDate = coupon.start_date ? new Date(coupon.start_date) : null;
    const endDate = coupon.end_date ? new Date(coupon.end_date) : null;

    if (!coupon.is_active) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
          <XCircle className="w-4 h-4 mr-2" />
          Inactive
        </span>
      );
    }

    if (startDate && startDate > now) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          <Clock className="w-4 h-4 mr-2" />
          Coming Soon
        </span>
      );
    }

    if (endDate && endDate < now) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          <XCircle className="w-4 h-4 mr-2" />
          Expired
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
        <CheckCircle className="w-4 h-4 mr-2" />
        Active
      </span>
    );
  };

  // Get discount display
  const getDiscountDisplay = (coupon) => {
    switch (coupon.type) {
      case 'percentage':
        return (
          <div className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400">
            {coupon.discount_percentage}% OFF
          </div>
        );
      case 'fixed_amount':
        return (
          <div className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400">
            ₹{coupon.discount_amount} OFF
          </div>
        );
      case 'buy_one_get_one':
        return (
          <div className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400">
            BUY 1 GET 1
          </div>
        );
      case 'free_shipping':
        return (
          <div className="text-2xl sm:text-3xl font-bold text-orange-600 dark:text-orange-400">
            FREE SHIPPING
          </div>
        );
      default:
        return (
          <div className="text-2xl sm:text-3xl font-bold text-gray-600 dark:text-gray-400">
            DISCOUNT
          </div>
        );
    }
  };

  // Get time remaining
  const getTimeRemaining = (endDate) => {
    if (!endDate) return null;
    
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    
    if (diff <= 0) return null;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} left`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} left`;
    } else {
      return 'Less than 1 hour left';
    }
  };

  // Filter coupons based on active tab
  const filteredCoupons = coupons.filter(coupon => {
    const now = new Date();
    const startDate = coupon.start_date ? new Date(coupon.start_date) : null;
    const endDate = coupon.end_date ? new Date(coupon.end_date) : null;
    
    if (activeTab === 'available') {
      return coupon.is_active && 
             (!startDate || startDate <= now) && 
             (!endDate || endDate >= now);
    } else if (activeTab === 'expired') {
      return endDate && endDate < now;
    } else if (activeTab === 'upcoming') {
      return startDate && startDate > now;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8">
        <div className="flex items-center justify-center h-24 sm:h-32">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-2 border-blue-600"></div>
          <span className="ml-3 sm:ml-4 text-base sm:text-lg font-semibold text-gray-600 dark:text-gray-400">Loading coupons...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">My Coupons</h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">Available discount codes and promotional offers</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-2">
        <div className="flex space-x-1 sm:space-x-2">
          <button
            onClick={() => setActiveTab('available')}
            className={`flex-1 px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-sm sm:text-base font-bold rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation ${
              activeTab === 'available'
                ? 'card-3d bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-glow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/10'
            }`}
          >
            <span className="hidden sm:inline">Available</span>
            <span className="sm:hidden">Available</span>
            <span className="ml-1">({coupons.filter(c => {
              const now = new Date();
              const startDate = c.start_date ? new Date(c.start_date) : null;
              const endDate = c.end_date ? new Date(c.end_date) : null;
              return c.is_active && (!startDate || startDate <= now) && (!endDate || endDate >= now);
            }).length})</span>
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-sm sm:text-base font-bold rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation ${
              activeTab === 'upcoming'
                ? 'card-3d bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-glow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/10'
            }`}
          >
            <span className="hidden sm:inline">Upcoming</span>
            <span className="sm:hidden">Upcoming</span>
            <span className="ml-1">({coupons.filter(c => {
              const startDate = c.start_date ? new Date(c.start_date) : null;
              return startDate && startDate > new Date();
            }).length})</span>
          </button>
          <button
            onClick={() => setActiveTab('expired')}
            className={`flex-1 px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-sm sm:text-base font-bold rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation ${
              activeTab === 'expired'
                ? 'card-3d bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-glow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/10'
            }`}
          >
            <span className="hidden sm:inline">Expired</span>
            <span className="sm:hidden">Expired</span>
            <span className="ml-1">({coupons.filter(c => {
              const endDate = c.end_date ? new Date(c.end_date) : null;
              return endDate && endDate < new Date();
            }).length})</span>
          </button>
        </div>
      </div>

      {/* Coupons Grid */}
      {filteredCoupons.length === 0 ? (
        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-6 sm:p-8 md:p-12">
          <div className="text-center">
            {activeTab === 'available' && (
              <>
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                  <Gift className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">No Available Coupons</h3>
                <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
                  You don't have any active coupons at the moment. Check back later for new offers!
                </p>
              </>
            )}
            {activeTab === 'upcoming' && (
              <>
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                  <Clock className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">No Upcoming Coupons</h3>
                <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
                  No upcoming coupons scheduled at the moment.
                </p>
              </>
            )}
            {activeTab === 'expired' && (
              <>
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                  <XCircle className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">No Expired Coupons</h3>
                <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
                  You don't have any expired coupons.
                </p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {filteredCoupons.map((coupon) => (
            <div
              key={coupon.id}
              className={`card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8 transition-all duration-300 hover:scale-105 relative ${
                activeTab === 'expired' 
                  ? 'opacity-75' 
                  : ''
              }`}
            >
              {/* Status Badge */}
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 md:top-6 md:right-6">
                {getStatusBadge(coupon)}
              </div>

              {/* Coupon Type Icon */}
              <div className="flex items-center justify-center mb-4 sm:mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                  {getTypeIcon(coupon.type)}
                </div>
              </div>

              {/* Discount Display */}
              <div className="text-center mb-4 sm:mb-6">
                {getDiscountDisplay(coupon)}
              </div>

              {/* Coupon Code */}
              <div className="text-center mb-4 sm:mb-6">
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <span className="font-mono text-sm sm:text-base md:text-xl font-bold text-gray-900 dark:text-white bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 px-3 sm:px-4 py-2 rounded-xl sm:rounded-2xl shadow-lg">
                    {coupon.code}
                  </span>
                  <button
                    onClick={() => copyCode(coupon.code, coupon.id)}
                    className="p-2 sm:p-3 text-gray-400 hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation"
                    title="Copy code"
                  >
                    <Copy className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Coupon Name */}
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white text-center mb-2 sm:mb-3">
                {coupon.name}
              </h3>

              {/* Description */}
              {coupon.description && (
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 text-center mb-4 sm:mb-6">
                  {coupon.description}
                </p>
              )}

              {/* Conditions */}
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                {coupon.min_cart_value && (
                  <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <Info className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                    Min. cart value: ₹{coupon.min_cart_value}
                  </div>
                )}
                {coupon.max_cart_value && (
                  <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <Info className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                    Max. cart value: ₹{coupon.max_cart_value}
                  </div>
                )}
                {coupon.max_usage_per_user > 1 && (
                  <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <Info className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                    Use up to {coupon.max_usage_per_user} times
                  </div>
                )}
              </div>

              {/* Expiry Info */}
              <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-3 sm:pt-4">
                {coupon.end_date ? (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs sm:text-sm gap-1 sm:gap-0">
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                      Expires: {new Date(coupon.end_date).toLocaleDateString()}
                    </div>
                    {activeTab === 'available' && (
                      <div className="text-orange-600 font-bold text-xs sm:text-sm">
                        {getTimeRemaining(coupon.end_date)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center text-xs sm:text-sm text-green-600 dark:text-green-400">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                    No expiry date
                  </div>
                )}
              </div>

              {/* Inline copied message */}
              {copiedCouponId === coupon.id && (
                <div className="mt-3 sm:mt-4 mb-2 text-xs sm:text-sm font-semibold text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl sm:rounded-2xl px-3 sm:px-4 py-2 sm:py-3 text-center">
                  Coupon code copied! You can paste it at checkout.
                </div>
              )}

              {/* Use Coupon Button */}
              {activeTab === 'available' && (
                <button
                  onClick={() => {
                    copyCode(coupon.code, coupon.id);
                    // You can add navigation to checkout here
                    toast.success('Coupon code copied! Use it during checkout.');
                  }}
                  className="w-full mt-4 sm:mt-6 card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base touch-manipulation"
                >
                  <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                  Use Coupon
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Help Text */}
      <div className="card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-blue-200 dark:border-blue-800 p-4 sm:p-6 md:p-8">
        <div className="flex items-start">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg mr-3 sm:mr-4 flex-shrink-0">
            <Info className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="text-sm sm:text-base text-blue-800 dark:text-blue-200">
            <p className="font-bold mb-2 sm:mb-3">How to use your coupons:</p>
            <ol className="list-decimal list-inside space-y-1 sm:space-y-2 text-xs sm:text-sm">
              <li>Copy the coupon code by clicking the copy icon</li>
              <li>Add items to your cart</li>
              <li>During checkout, paste the coupon code in the discount field</li>
              <li>The discount will be automatically applied to your order</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCoupons;
